<template>
  <div id="project-features">
    <div id="project-features-content">
      <!-- PREMIÈRE COLONNE -->
      <div class="column-left">
        <div
          id="project-features-header"
        >
          <!-- TITLE, Count & Toggle list|map -->
          <div class="infos">
            <h1 class="inline">
              Signalement{{ featuresCountDisplay > 1 ? "s" : "" }}
            </h1>
            <div class="ui large teal label">
              <i class="marker icon" />
              {{ featuresCountDisplay }}
            </div>
          </div>
          <div class="ui secondary menu no-margin">
            <a
              id="show-map"
              :class="['item no-margin', { 'active': showMap }]"
              data-tab="map"
              data-tooltip="Carte"
              data-position="bottom right"
              @click="setShowMap(true)"
            >
              <i
                class="map fitted icon"
                aria-hidden="true"
              />
            </a>
            <a
              id="show-list"
              :class="['item no-margin', { 'active': !showMap }]"
              data-tab="list"
              data-tooltip="Liste"
              data-position="bottom left"
              @click="setShowMap(false)"
            >
              <i
                class="list fitted icon"
                aria-hidden="true"
              />
            </a>
          </div>
        </div>

        <FeaturesListAndMapFilters
          :pagination="pagination"
          :form="form"
          @set-filter="setFilter"
          @set-custom-filter="setCustomFilter"
          @reset-form="resetForm"
        />
      </div>
      <!-- DEUXIÈME COLONNE -->
      <div class="column-right">
        <!-- ACTION buttons -->
        <div
          id="project-feature-menu"
          class="ui secondary menu"
        >
          <!-- Button EDIT STATUS -->
          <div
            v-if="isOnline && !showMap && permissions.can_update_feature"
            id="edit-button"
            :class="[
              'ui dropdown button button-hover-primary',
              {'disabled': !hasSelection}
            ]"
            data-tooltip="Modifier le statut des signalements"
            data-position="bottom left"
            @click="toggleModifyStatusDropdown"
          >
            <i
              class="edit fitted icon"
              aria-hidden="true"
            />
            <div
              v-if="showModifyStatusOptions"
              class="menu right transition visible"
            >
              <div class="header">
                Modifier le statut des Signalements
              </div>
              <div class="scrolling menu text-wrap">
                <span
                  v-for="status in availableStatus"
                  :key="status.value"
                  class="item"
                  @click="toggleUpdateStatusModal(status.value)"
                >
                  {{ status.name }}
                </span>
              </div>
            </div>
          </div>
          <!-- Button DELETE feature -->
          <div
            v-if="isOnline && !showMap && permissions.can_delete_feature"
            :class="[
              'ui button button-hover-red',
              {'disabled': !hasSelection}
            ]"
            data-tooltip="Supprimer les signalements sélectionnés"
            data-position="bottom left"
            @click="modalAction = 'delete'"
          >
            <i
              class="grey trash fitted icon"
              aria-hidden="true"
            />
          </div>

          <div class="right menu">
            <!-- Button EDIT ATTRIBUTES -->
            <div v-if="isOnline && !showMap && permissions.can_update_feature">
              <div v-if="availableFeatureTypesForEdit.length === 1">
                <router-link
                  :to="{
                    name: 'editer-attribut-signalement',
                    params: {
                      slug_type_signal: availableFeatureTypesForEdit[0].slug,
                      queryparams: queryparams
                    }
                  }"
                  :class="[
                    'ui button button-hover-primary',
                    {'disabled': !hasSelection}
                  ]"
                  data-tooltip="Modifier les attributs des signalements"
                  data-position="bottom right"
                >
                  <i
                    class="tags fitted icon"
                    aria-hidden="true"
                  />
                  {{ availableFeatureTypesForEdit[0].title }} ({{ availableFeatureTypesForEdit[0].count }})
                </router-link>
              </div>
              <div
                v-else
                :class="[
                  'ui dropdown button button-hover-primary',
                  {'disabled': !hasSelection}
                ]"
                data-tooltip="Modifier les attributs des signalements"
                data-position="bottom right"
                @click="toggleEditAttributesDropdown"
              >
                <i
                  class="tags fitted icon"
                  aria-hidden="true"
                />
                <div
                  v-if="showEditAttributesOptions"
                  class="menu left transition visible"
                >
                  <div class="header">
                    Modifier les signalements du type:
                  </div>
                  <div class="scrolling menu text-wrap">
                    <router-link
                      v-for="(type, index) in availableFeatureTypesForEdit"
                      :key="type.slug + index"
                      :to="{
                        name: 'editer-attribut-signalement',
                        params: {
                          slug_type_signal: type.slug,
                          queryparams: queryparams
                        }
                      }"
                      class="item"
                    >
                      {{ type.title }} ({{ type.count }})
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
            <!-- Button EXPORT PDF -->
            <div
              v-if="isOnline && !showMap"
              :class="[
                'ui button button-hover-primary',
                {'disabled': !hasSelection}
              ]"
              data-tooltip="Exporter les signalements en PDF"
              data-position="bottom right"
              @click="isModalExportOpen = true"
            >
              <i
                class="file pdf fitted icon"
                aria-hidden="true"
              />
            </div>

            <!-- Button ADD feature -->
            <div
              v-if="filteredFeatureTypeChoices.length > 0"
              class="ui dropdown button button-hover-green"
              data-tooltip="Ajouter un signalement"
              data-position="bottom right"
              @click="toggleAddFeatureDropdown"
            >
              <i
                class="plus fitted icon"
                aria-hidden="true"
              />
              <div
                v-if="showAddFeatureOptions"
                class="menu left transition visible"
              >
                <div class="header">
                  Sélectionner un type de signalement
                </div>
                <div class="scrolling menu text-wrap">
                  <router-link
                    v-for="(type, index) in filteredFeatureTypeChoices"
                    :key="type.slug + index"
                    :to="{
                      name: 'ajouter-signalement',
                      params: { slug_type_signal: type.slug },
                    }"
                    class="item"
                  >
                    {{ type.title }}
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- CARTE -->
        <div
          :class="['ui tab active map-container', { 'visible': showMap }]"
          data-tab="map"
        >
          <div
            id="map"
            ref="map"
          >
            <SidebarLayers
              v-if="basemaps && map"
              ref="sidebar"
            />
            <Geolocation />
            <Geocoder />
            <ItineraryButton />
          </div>
          <MapTilesLoader />
          <!-- POPUP -->
          <div
            id="popup"
            class="ol-popup"
          >
            <a
              id="popup-closer"
              href="#"
              class="ol-popup-closer"
            />
            <div
              id="popup-content"
            />
          </div>
        </div>
        <!-- LISTE -->
        <FeatureListTable
          v-show="!showMap"
          :paginated-features="paginatedFeatures"
          :page-numbers="pageNumbers"
          :features-count="featuresCount"
          :pagination="pagination"
          :sort="sort"
          :queryparams="queryparams"
          :loading="loading"
          @update:page="handlePageChange"
          @update:sort="handleSortChange"
        />
      </div>
      <!-- MODALS -->
      <ConfirmBulkActionModal
        v-if="modalAction !== null"
        :action="modalAction"
        :total-features-count="selectedFeaturesCount"
        @confirm-delete="deleteFeatures"
        @confirm-update-status="modifyStatus"
        @close="modalAction = null"
      />
      <FeaturesPdfExportModal
        :queryparams="queryparams"
        :active="isModalExportOpen"
        @close="isModalExportOpen = false"
      />
    </div>
  </div>
</template>

<script>

import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import mapService from '@/services/map-service';
import Geocoder from '@/components/Map/Geocoder';
import featureAPI from '@/services/feature-api';

import FeaturesPdfExportModal from '@/components/FeaturesPdfExportModal';
import FeaturesListAndMapFilters from '@/components/Project/FeaturesListAndMap/FeaturesListAndMapFilters';
import FeatureListTable from '@/components/Project/FeaturesListAndMap/FeatureListTable';
import ConfirmBulkActionModal from '@/components/Project/FeaturesListAndMap/ConfirmBulkActionModal';
import SidebarLayers from '@/components/Map/SidebarLayers';
import Geolocation from '@/components/Map/Geolocation';
import ItineraryButton from '@/components/Map/ItineraryButton';
import MapTilesLoader from '@/components/Map/MapTilesLoader';
import { allowedStatus2change, transformCustomFilters, handleApiError } from '@/utils';

export default {
  name: 'FeaturesListAndMap',

  components: {
    FeaturesPdfExportModal,
    ConfirmBulkActionModal,
    FeatureListTable,
    FeaturesListAndMapFilters,
    Geocoder,
    Geolocation,
    ItineraryButton,
    SidebarLayers,
    MapTilesLoader,
  },

  data() {
    return {
      currentLayer: null,
      featuresCount: 0,
      featuresWithGeomCount:0,
      form: {
        title: null,
        feature_type_slug: [],
        status__value: [],
        created_on__gte: null,
        created_on__lte: null,
        creator_id: null,
        featureData: {}
      },
      isModalExportOpen: false,
      loading: false,
      lat: null,
      lng: null,
      map: null,
      modalAction: null,
      paginatedFeatures: [],
      pagination: {
        currentPage: 1,
        pagesize: 15,
        start: 0,
        end: 15,
      },
      projectSlug: this.$route.params.slug,
      queryparams: {},
      selectedStatus: null,
      showAddFeatureOptions: false,
      showEditAttributesOptions: false,
      showModifyStatusOptions: false,
      showMap: true,
      sort: {
        column: 'updated_on',
        ascending: true,
      },
      zoom: null,
    };
  },

  computed: {
    ...mapGetters([
      'permissions',
    ]),
    ...mapGetters('feature', [
      'massSelectionPayload',
      'checkedFeaturesSet',
      'hasSelection',
      'selectedTypeCounts',
      'selectedFeaturesCount',
      'uncheckedFeaturesSet',
    ]),
    ...mapState([
      'configuration',
      'isOnline',
      'user',
      'USER_LEVEL_PROJECTS',
    ]),
    ...mapState('projects', [
      'project',
    ]),
    ...mapState('feature', [
      'checkedFeatures',
      'uncheckedFeatures',
      'allSelected',
    ]),
    ...mapState('feature-type', [
      'feature_types',
    ]),
    ...mapState('map', [
      'basemaps',
    ]),
    API_BASE_URL() {
      return this.configuration.VUE_APP_DJANGO_API_BASE;
    },
    userLevel() {
      return this.USER_LEVEL_PROJECTS[this.$route.params.slug];
    },
    availableStatus() {
      if (this.project && this.user) {
        const isModerate = this.project.moderation;
        const isOwnFeature = true; // In this case, the contributor is always the author of the features they can edit
        return allowedStatus2change(this.user, isModerate, this.userLevel, isOwnFeature, this.onlyPublished);
      }
      return [];
    },
    pageNumbers() {
      return this.createPagesArray(this.featuresCount, this.pagination.pagesize);
    },
    mvtFeatureLimit() {
      return Number(this.configuration.VUE_APP_MVT_FEATURE_LIMIT) || 1000;
    },
    featuresCountDisplay() {
      return this.showMap ? this.featuresWithGeomCount : this.featuresCount;
    },
    filteredFeatureTypeChoices() {
      return this.feature_types.filter((ft) => !ft.geom_type.includes('multi'));
    },
    availableFeatureTypesForEdit() {
      return this.feature_types
        .filter(featureType => this.selectedTypeCounts[featureType.slug] !== undefined)
        .map(featureType => ({
          title: featureType.title,
          slug: featureType.slug,
          count: this.selectedTypeCounts[featureType.slug]
        }));
    },
  },

  watch: {
    isOnline(newValue, oldValue) {
      if (newValue != oldValue && !newValue) {
        this.DISPLAY_MESSAGE({
          comment: 'Les signalements du projet non mis en cache ne sont pas accessibles en mode déconnecté',
        });
      }
    },
  },

  mounted() {
    if (this.$route.query.vue === 'liste') {
      this.showMap = false;
    }
    if (!this.project) {
      // Chargements des features et infos projet en cas d'arrivée directe sur la page ou de refresh
      Promise.all([
        this.$store.dispatch('projects/GET_PROJECT', this.projectSlug),
        this.$store.dispatch('projects/GET_PROJECT_INFO', this.projectSlug)
      ]).then(()=> this.initPage());
    } else {
      this.initPage();
    }
    window.addEventListener('mousedown', this.clickOutsideDropdowns);
  },

  destroyed() {
    //* allow user to change page if ever stuck on loader
    this.loading = false;
    window.removeEventListener('mousedown', this.clickOutsideDropdowns);
  },

  methods: {
    ...mapActions('feature', [
      'DELETE_FEATURE',
    ]),
    ...mapMutations('feature', [
      'SET_TYPE_COUNTS'
    ]),
    ...mapMutations([
      'DISPLAY_MESSAGE',
    ]),
    ...mapMutations('feature', [
      'SET_CHECKED_FEATURES',
      'SET_UNCHECKED_FEATURES',
    ]),

    initPage() {
      this.sort = {
        column: this.project.feature_browsing_default_sort.replace('-', ''),
        ascending: this.project.feature_browsing_default_sort.includes('-')
      };
      this.initMap();
      this.fetchPagedFeatures();
    },

    initMap() {
      this.zoom = this.$route.query.zoom || '';
      this.lat = this.$route.query.lat || '';
      this.lng = this.$route.query.lng || '';

      const mapDefaultViewCenter = this.configuration.DEFAULT_MAP_VIEW.center;
      const mapDefaultViewZoom = this.configuration.DEFAULT_MAP_VIEW.zoom;

      this.map = mapService.createMap(this.$refs.map, {
        zoom: this.zoom,
        lat: this.lat,
        lng: this.lng,
        mapDefaultViewCenter,
        mapDefaultViewZoom,
        maxZoom: this.project.map_max_zoom_level,
        interactions : { doubleClickZoom :false, mouseWheelZoom:true, dragPan:true },
        fullScreenControl: true,
        geolocationControl: true,
      });

      this.$nextTick(() => {
        const mvtUrl = `${this.API_BASE_URL}features.mvt`;
        mapService.addVectorTileLayer({
          url: mvtUrl,
          project_slug: this.projectSlug,
          featureTypes: this.feature_types,
          queryParams: this.getMvtQueryParams(),
        });
      });
    },

    getMvtQueryParams() {
      const excluded = ['offset'];
      const params = {};
      for (const [key, value] of Object.entries(this.queryparams)) {
        if (excluded.includes(key)) {
          continue;
        }
        if (value !== null && value !== undefined && value !== '') {
          params[key] = Array.isArray(value) ? value.join(',') : value;
        }
      }
      if (!params.ordering) {
        params.ordering = '-created_on';
      }
      if (this.mvtFeatureLimit > 0) {
        params.limit = this.mvtFeatureLimit;
      }
      return params;
    },

    onFilterChange() {
      if (mapService.getMap() && mapService.mvtLayer) {
        mapService.updateMvtQueryParams(this.getMvtQueryParams());
      }
    },

    fetchBboxNfit(queryString) {
      featureAPI
        .getFeaturesBbox(this.projectSlug, queryString)
        .then((bbox) => {
          if (bbox) {
            mapService.fitBounds(bbox);
          }
        });
    },

    setShowMap(newValue) {
      this.showMap = newValue;
      // expanded sidebar is visible under the list, even when the map is closed (position:absolute), solved by closing it when switching to list
      if (newValue === false && this.$refs.sidebar) this.$refs.sidebar.toggleSidebar(false);
    },

    setFilter(newFilter) {
      const filter = Object.keys(newFilter)[0];
      let value = Object.values(newFilter)[0];
      if (value && Array.isArray(value)) {
        value = value.map(el => el.value);
      }
      this.form[filter] = value;
      this.refreshResults();
    },

    setCustomFilter(customFilters) {
      this.form.featureData = transformCustomFilters(customFilters);
      this.refreshResults();
    },

    getAvalaibleField(orderField) {
      let result = orderField;
      if (orderField === 'display_creator') {
        result = 'creator';
      } else if (orderField === 'display_last_editor') {
        result = 'last_editor';
      }
      return result;
    },
    /**
     * Updates the query parameters based on the current state of the pagination and form filters.
     * Used in an API request and to filter features on mvt tiles.
     */
    updateQueryParams() {
      // Reset queryparams
      this.queryparams = {};
      // Update pagination offset
      this.queryparams['offset'] = this.pagination.start;

      // Add classic filters (e.g., status, created_on)
      for (const fieldName in this.form) {
        // Ignore featureData for now (handled separately)
        if (fieldName === 'featureData') continue;

        const value = this.form[fieldName];
        if (value && !(Array.isArray(value) && value.length === 0)) {
          this.queryparams[fieldName] = value;
        }
      }
      // Add custom filters from featureData
      if (this.form.featureData) {
        for (const [key, value] of Object.entries(this.form.featureData)) {
          if (value !== null && value !== undefined) {
            this.queryparams[`feature_data__${key}`] = value;
          }
        }
      }
      // Update sorting
      this.queryparams['ordering'] = `${this.sort.ascending ? '' : '-'}${this.getAvalaibleField(this.sort.column)}`;
    },

    /**
     * Fetches paginated feature data from the API.
     * This function is called to retrieve a specific page of features based on the current pagination settings and any applied filters.
     * If the application is offline, it displays a message and does not proceed with the API call.
     */
    fetchPagedFeatures() {
      // Check if the application is offline
      if (!this.isOnline) {
        this.DISPLAY_MESSAGE({
          comment: 'Project features not cached are not accessible offline',
        });
        return;
      }

      this.loading = true; // show loader

      // Update query parameters based on filters
      this.updateQueryParams();

      const queryString = new URLSearchParams(this.queryparams).toString();
      // Build the paginated request URL
      const url = `${this.API_BASE_URL}projects/${this.projectSlug}/feature-paginated/?limit=${this.pagination.pagesize}&${queryString}`;

      featureAPI.getPaginatedFeatures(url)
        .then((data) => {
          // Handle cancellation
          if (!data || data.cancelled) {
            console.debug('fetchPagedFeatures request cancelled or no data');
            return;
          }
          // Update component state with received data
          this.featuresCount = data.count;
          this.featuresWithGeomCount = data.geom_count;
          this.previous = data.previous;
          this.next = data.next;
          this.paginatedFeatures = data.results || [];
          this.SET_TYPE_COUNTS(data.type_counts);

          // If features are present, calculate BBOX and adjust view
          if (this.paginatedFeatures.length) {
            this.fetchBboxNfit(queryString);
          }
          // Actions related to filter changes
          this.onFilterChange();
        })
        .catch((error) => {
          console.error('Error loading paginated features:', error);
        })
        .finally(() => {
          // Ensure loader is always hidden, even in case of error or cancellation
          this.loading = false;
        });
    },

    async modifyStatus() {
      try {
        // Build the query string added to the URL
        const queryString = new URLSearchParams(this.queryparams).toString();

        // Build data to send
        const requestData = {
          status: this.selectedStatus,
          ...this.massSelectionPayload
        };

        // Call API with prepared data
        const response = await featureAPI.projectFeatureBulkUpdate(
          this.projectSlug,
          queryString,
          requestData
        );

        if (response?.data) {
          this.DISPLAY_MESSAGE({
            comment: response.data.message,
            level: response.data.level,
          });
        }
        if (response?.status === 200) {
          this.SET_UNCHECKED_FEATURES([]);
          this.SET_CHECKED_FEATURES([]);
        }
      } catch (error) {
        handleApiError(error, this.DISPLAY_MESSAGE);
      } finally {
        this.refreshResults();
        this.modalAction = null;
      }
    },

    async deleteFeatures() {
      try {
        // Build the query string concatenated with the URL
        const queryString = new URLSearchParams(this.queryparams).toString();

        // Build data to send
        const requestData = { ...this.massSelectionPayload };

        // Call API with prepared data
        const response = await featureAPI.projectFeatureBulkDelete(
          this.projectSlug,
          queryString,
          requestData
        );

        if (response?.data) {
          this.DISPLAY_MESSAGE({
            comment: response.data.message,
            level: response.data.level,
          });
        }
        if (response?.status === 200) {
          this.SET_UNCHECKED_FEATURES([]);
          this.SET_CHECKED_FEATURES([]);
        }
      } catch (error) {
        handleApiError(error, this.DISPLAY_MESSAGE);
      } finally {
        this.refreshResults();
        this.modalAction = null;
      }
    },

    resetForm() {
      this.form.title = null;
      this.form.feature_type_slug = [];
      this.form.status__value = [];
      this.form.created_on__gte = null;
      this.form.created_on__lte = null;
      this.form.creator_id = null;
      this.form.featureData = {};
      this.refreshResults();
    },
    resetPagination() {
      this.pagination.currentPage = 1;
      this.pagination.pagesize = 15;
      this.pagination.start = 0;
      this.pagination.end = 15;
    },
    refreshResults() {
      this.resetPagination();
      this.fetchPagedFeatures();
    },

    toggleUpdateStatusModal(newStatus) {
      this.selectedStatus = newStatus;
      this.modalAction = 'update-status';
    },
    toggleAddFeatureDropdown() {
      this.showAddFeatureOptions = !this.showAddFeatureOptions;
      this.showEditAttributesOptions = false;
      this.showModifyStatusOptions = false;
    },
    toggleEditAttributesDropdown() {
      this.showEditAttributesOptions = !this.showEditAttributesOptions;
      this.showAddFeatureOptions = false;
      this.showModifyStatusOptions = false;
    },
    toggleModifyStatusDropdown() {
      this.showModifyStatusOptions = !this.showModifyStatusOptions;
      this.showEditAttributesOptions = false;
      this.showAddFeatureOptions = false;
    },
    clickOutsideDropdowns(e) {
      if (!e.target.closest('#project-feature-menu')) {
        this.showAddFeatureOptions = false;
        this.showEditAttributesOptions = false;
        this.showModifyStatusOptions = false;
      }
    },

    //* ************* Pagination for table *************** *//

    createPagesArray(featuresCount, pagesize) {
      const totalPages = Math.ceil(
        featuresCount / pagesize
      );
      return [...Array(totalPages).keys()].map((pageNumb) => {
        ++pageNumb;
        return pageNumb;
      });
    },
    handlePageChange(page) {
      if (page === 'next') {
        this.toNextPage();
      } else if (page === 'previous') {
        this.toPreviousPage();
      } else if (typeof page === 'number') {
        this.toPage(page);
      }
    },
    handleSortChange(sort) {
      this.sort = sort;
      this.fetchPagedFeatures();
    },
    toPage(pageNumber) {
      const toAddOrRemove = (pageNumber - this.pagination.currentPage) * this.pagination.pagesize;
      this.pagination.start += toAddOrRemove;
      this.pagination.end += toAddOrRemove;
      this.pagination.currentPage = pageNumber;
      this.fetchPagedFeatures();
    },
    toPreviousPage() {
      if (this.pagination.currentPage === 1 && this.pagination.start <= 0) return;
      this.pagination.start -= this.pagination.pagesize;
      this.pagination.end -= this.pagination.pagesize;
      this.pagination.currentPage -= 1;
      this.fetchPagedFeatures();
    },
    toNextPage() {
      if (this.pagination.currentPage === this.pageNumbers.length &&
        this.pagination.end >= this.featuresCount) return;
      this.pagination.start += this.pagination.pagesize;
      this.pagination.end += this.pagination.pagesize;
      this.pagination.currentPage += 1;
      this.fetchPagedFeatures();
    },
  },
};
</script>


<style lang="less" scoped>
#project-features {
  max-width: initial;
  margin-top: 1em;
}

#project-features-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  > div {
    margin: 1em;
  }
  > div:first-of-type {
    margin-left: 0;
  }
  > div:last-of-type {
    margin-right: 0;
  }
  .infos {
    display: flex;
    justify-content: baseline;
    h1 {
      margin: auto 0;
    }
    .label {
      margin: 0 .75em;
      border-radius: 7px;
      font-size: 1.2rem;
      white-space: nowrap;
    }
  }
}
#project-feature-menu {
  min-height: 65px; // keep height before buttons fill in
  margin: 0;
  > div {
    margin-top: 1em;
    margin-bottom: 1em;
  }
  > div:first-of-type {
    margin-left: 0;
  }
  > div:last-of-type {
    margin-right: 0;
  }
  .ui.dropdown .menu .left.menu,
  .ui.dropdown>.left.menu .menu {
    margin-right: 0 !important;
  }
  .ui.dropdown, .ui.button {
      z-index: 9;
  }
}
#project-features-content {
  display: flex;
  position: relative;

}
@media screen and (max-width: 767px) {
  #project-features-content {
    flex-direction: column;
  }
}
@media screen and (min-width: 768px) {
  .column-left {
    flex: 0 1 30%;
    padding-right: .5em;
  }
  .column-right {
    flex: 1 1 70%;
    padding-left: .5em;
  }
}

#map {
  width: 100%;
  min-height: 310px;
  border: 1px solid grey;
}
@media screen and (min-width: 768px) {
  #map {
    height: calc(100vh - 170px - 1em);
  }
}
.map-container {
  transform: translateX(-50%);
  margin-left: 50%;
  visibility: hidden;
  position: absolute;
}
.map-container:not(.visible) #map {
  min-height: 0;
}
.map-container.visible {
  visibility: visible;
  position: relative;
  width: 100%;
  .sidebar-container {
    left: -250px;
  }
  .sidebar-container.expanded {
    left: 0;
  }
}

div.geolocation-container {
  // each button have (more or less depends on borders) .5em space between
  // zoom buttons are 60px high, geolocation and full screen button is 34px high with borders
  top: calc(1.3em + 60px + 34px);
}
div.itinerary-container {
  top: calc(1.6em + 60px + 34px + 34px + 38px);
}
</style>
